import { Icon, ToolTip } from '@greystar/designsystem';
import {
  getFeeLineLabel,
  getFeeLinePrice,
  getFeeSum,
  getMonthlyAdditionalPetFees,
  isFeeMonthly,
  useAnalytics,
  useFormattedAddress,
  useFeesAnalytics,
} from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import FeeLineProps from './FeeLine.props';
import styles from './feeLine.module.scss';

const FeeLine = (props: FeeLineProps) => {
  const moveInDate = props.feeState.selectedMoveInDate;
  const price = getFeeLinePrice(props.feeState, props.fee, props.proration);
  const feeSum = getFeeSum(props.feeState, props.fee, props.proration);
  const label = getFeeLineLabel(moveInDate, props.fee);

  const analytics = useAnalytics();
  const { sitecoreContext } = useSitecoreContext();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const propertyId = property.id ?? '';
  const propertyName = property.name ?? '';
  const state = property?.location?.state;
  const property_location = useFormattedAddress(property.location);
  const { mergeAnalyticsData } = useFeesAnalytics();

  const handleTooltipClick = () => {
    analytics.trackEvent('tooltip', {
      action_type: 'click',
      state: state,
      property_location,
      property_id: propertyId,
      property_name: propertyName,
      content_group: props.groupName ?? 'One-time costs',
      module_name: 'fees section',
      inpage_location: 'middle',
      click_text: label ?? '',
      destination_url: '',
    });
  };

  const description = props.proration
    ? 'Estimated prorated amount includes applicable portion of base rent and mandatory and user-selected optional fees eligible for proration and full amount of mandatory and user-selected.'
    : props.fee?.tooltipLabel ?? '';

  const renderAdditionalPetFees = () => {
    const showAdditionlPetFees =
      props.isSummaryStep &&
      props.fee?.group === 'pets_other' &&
      isFeeMonthly(props.fee) &&
      !props.isAdditionalPetFee;
    if (!showAdditionlPetFees) return <></>;

    const monthlyAdditionalPetFees = getMonthlyAdditionalPetFees(props.feeState.allFees ?? []);
    if (!monthlyAdditionalPetFees?.length) return <></>;

    return monthlyAdditionalPetFees.map((petFee) => (
      <FeeLine
        className={styles['feeLine-additionalPetFee']}
        {...props}
        key={petFee.id}
        fee={petFee}
        isAdditionalPetFee={true}
      />
    ));
  };

  const updateTooltipData = () => {
    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_tooltip',
        action_type: 'click',
        tooltip: label,
        click_text: label,
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_tooltip', data);
      },
    });
  };

  return (
    <div className={`${styles['feeLine']} ${props.className}`}>
      <div
        className={`
        ${styles['feeLine-content']}
        ${props.isSummaryStep ? styles['feeLine-content--summary'] : ''}
      `}
      >
        <div
          className={`${styles['feeLine-content-wrapper']} ${
            !props.isSummaryStep ? styles['feeLine-content-wrapper--rowGap'] : ''
          }`}
        >
          <div className={styles['feeLine-content-wrapper-label']}>
            <span className={styles['feeLine-content-wrapper-label-text']}>{label}</span>
            <ToolTip
              description={description}
              elementToHover={<Icon iconName="QuestionMark" size="sm" />}
              className={styles['feeLine-content-wrapper-label-tooltip']}
              feeline={true}
              toolTipClick={() => {
                updateTooltipData();
                handleTooltipClick();
              }}
            />

            {!props.isSummaryStep && (
              <div className={styles['feeLine-content-wrapper-price']}>{price}</div>
            )}
          </div>
        </div>
        {props.isSummaryStep && <div className={styles['feeLine-content-sum']}>{feeSum}</div>}
      </div>
      {renderAdditionalPetFees()}
    </div>
  );
};

export default FeeLine;
